import { Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react';
import clsx from 'clsx';
import { Fragment, useEffect, useState } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

// import ChevronIcon from '~/shared/assets/icons/chevron.svg';
import RefreshIcon from '~/shared/assets/icons/refresh.svg';
import CheckMarkIcon from '~/shared/assets/icons/checkmark.svg';
// import Button from '~/shared/components/Buttons';
import Typography from '~/shared/components/Typography';
import Button from '~/shared/components/ButtonsV2';

// import Button from '../common/Buttons';

interface RefreshOption {
  title: string;
  time: number;
}

interface RefreshFilterDropdownProps {
  onClickRefresh: () => void;
  onSelect: (selection: RefreshOption) => void;
  items?: RefreshOption[];
  selectedOptionIndex?: number;
  isLoading?: boolean;
}

const transitionProps = {
  enter: 'transition ease-out duration-100',
  enterFrom: 'transform opacity-0 scale-95',
  enterTo: 'transform opacity-100 scale-100',
  leave: 'transition ease-in duration-75',
  leaveFrom: 'transform opacity-100 scale-100',
  leaveTo: 'transform opacity-0 scale-95',
};

function RefreshFilterDropdown({
  onClickRefresh,
  onSelect,
  items,
  selectedOptionIndex = 0,
  isLoading,
}: RefreshFilterDropdownProps) {
  const [showCheckmark, setShowCheckmark] = useState(false);
  const selectedItem = items?.[selectedOptionIndex];

  const handleClick = (item: RefreshOption) => {
    onSelect(item);
  };

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (!isLoading) {
      setShowCheckmark(true);
      timer = setTimeout(() => {
        setShowCheckmark(false);
      }, 3000);
    }
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  return (
    <Menu as="div" className="relative flex">
      {({ open }) => (
        <>
          <div className="flex">
            <Button
              variant="third"
              size="md-responsive"
              className="w-[58px] border-r-0! rounded-r-none! group"
              onClick={onClickRefresh}
            >
              {showCheckmark ? (
                <CheckMarkIcon width={20} height={20} />
              ) : (
                <RefreshIcon
                  width={20}
                  height={20}
                  className={clsx(
                    isLoading ? 'animate-spin' : '',
                    'fill-primary-900 group-hover:fill-primary-500 group-active:fill-primary-300'
                  )}
                />
              )}
            </Button>

            <MenuButton
              className={clsx(
                'flex justify-center items-center px-1.5 group border border-gray-300 rounded-r-lg  cursor-pointer',
                open
                  ? 'bg-primary-20 border-primary-200'
                  : 'hover:border-primary-200 active:bg-primary-20 active:border-primary-200'
              )}
            >
              <div
                className={`h9 relative max-h-9 ${
                  selectedItem?.title && selectedItem?.title !== 'Off' ? 'mr-1' : ''
                }`}
              >
                {selectedItem?.title === 'Off' ? '' : selectedItem?.title}

                <ProgressBar
                  selectedItem={selectedItem}
                  onClickRefresh={onClickRefresh}
                  isLoading={isLoading}
                />
              </div>

              <ChevronDownIcon
                className={clsx(
                  'size-[14px] stroke-primary-900 stroke-3 cursor-pointer group-hover:stroke-primary-500 group-active:stroke-primary-300',
                  open ? 'rotate-180' : ''
                )}
              />
            </MenuButton>
          </div>

          <Transition as={Fragment} {...transitionProps}>
            <MenuItems className="absolute w-60 2xl:w-[291px] right-0 top-[42px] 2xl:top-[50px] origin-top-right font-light rounded-[8px] bg-white  border border-primary-70 shadow-filter-dropdown focus:outline-none z-50">
              <div className="">
                {items?.map((item) => (
                  <MenuItem
                    as={'button'}
                    key={item.title}
                    className={clsx(
                      'text-sm leading-6 w-full h-12 2xl:h-14 flex items-center  justify-between px-5 py-[18px]  border-gray-200 last:rounded-b-lg cursor-pointer hover:bg-primary-20 group',
                      items?.[0].title === item.title && 'rounded-t-lg ',
                      item.title === selectedItem?.title &&
                        'bg-primary-20 border border-primary-500',
                      item.title !== selectedItem?.title && 'border-b  border-gray-200'
                    )}
                    onClick={() => handleClick(item)}
                    // active={item.title === selectedItem?.title}
                  >
                    <Typography color="text-primary-900" variant="p3-reg">
                      {item.title}
                    </Typography>
                  </MenuItem>
                ))}
              </div>
            </MenuItems>
          </Transition>
        </>
      )}
    </Menu>
  );
}

export default RefreshFilterDropdown;

function ProgressBar({
  selectedItem,
  onClickRefresh,
  isLoading,
}: {
  selectedItem: RefreshOption | undefined;
  onClickRefresh: () => void;
  isLoading?: boolean;
}) {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!selectedItem || selectedItem.time <= 0 || isLoading) {
      return;
    }

    const intervalTime = 100;
    const totalSteps = selectedItem.time / intervalTime;
    let currentStep = 0;

    const intervalId = setInterval(() => {
      currentStep += 1;
      const newProgress = Math.floor((currentStep / totalSteps) * 100);

      if (newProgress >= 100) {
        clearInterval(intervalId);
        setProgress(100);
        onClickRefresh();
      } else {
        setProgress(newProgress);
      }
    }, intervalTime);

    return () => clearInterval(intervalId);
  }, [selectedItem, onClickRefresh, isLoading]);

  return (
    <div className="absolute bottom-[-6px] w-full h-[3px] bg-primary-50 rounded-lg overflow-hidden flex">
      <div className="h-full bg-primary-200" style={{ width: `${progress}%` }}></div>
    </div>
  );
}
